import { useCallback, useMemo } from 'react';

import useSearchQuery from '../useSearchQuery';
import {
  calculateNumberOfPages,
  getPageItems,
  getSafePageNumber,
} from './utils';

const DEFAULT_INITIAL_PAGE = 1;

export type PaginationOptions = {
  pageSize?: number;
};
const usePagination = <T>(
  items: T[] | undefined,
  options?: PaginationOptions,
) => {
  const { searchQuery, setSearchQuery } = useSearchQuery();
  const { pageSize = 20 } = options || {};
  const numberOfItems = items?.length || 0;

  const numberOfPages = useMemo(
    () => calculateNumberOfPages({ numberOfItems, pageSize }),
    [numberOfItems, pageSize],
  );

  // WARNING DO NOT USE VALUE DIRECTLY:
  // This value is unsafe and could be outside the valid bounds of our page numbers.
  const searchQueryPageNum = useMemo(
    () => (searchQuery?.page ? Number(searchQuery.page) : DEFAULT_INITIAL_PAGE),
    [searchQuery?.page],
  );

  // This value is derived from the unsafe value and will always be within the
  // bounds of our page numbers.
  const safeCurrentPageNumber = useMemo(
    () =>
      getSafePageNumber({
        pageNumber: searchQueryPageNum,
        numberOfPages,
      }),
    [searchQueryPageNum, numberOfPages],
  );

  // FLAGS
  const isNextPageAvailable = safeCurrentPageNumber < numberOfPages;
  const isPrevPageAvailable = safeCurrentPageNumber > 1;

  // NAVIGATION FUNCTIONS
  const next = useCallback(() => {
    if (!isNextPageAvailable) {
      return false;
    }

    setSearchQuery({ page: safeCurrentPageNumber + 1 });

    return true;
  }, [isNextPageAvailable, safeCurrentPageNumber]);

  const previous = useCallback(() => {
    if (!isPrevPageAvailable) {
      return false;
    }

    setSearchQuery({ page: safeCurrentPageNumber - 1 });

    return true;
  }, [isPrevPageAvailable, safeCurrentPageNumber]);

  const goTo = useCallback(
    (pageNumber: number) => {
      if (pageNumber > numberOfPages || pageNumber < 1) {
        return false;
      }

      setSearchQuery({ page: pageNumber });

      return true;
    },
    [numberOfPages],
  );

  // CURRENT ITEMS
  const currentPageItems = useMemo(
    () => getPageItems({ items, pageNumber: safeCurrentPageNumber, pageSize }),
    [safeCurrentPageNumber, items, pageSize],
  );

  return {
    currentPageNumber: safeCurrentPageNumber,
    currentPageItems,
    numberOfPages,
    isNextPageAvailable,
    isPrevPageAvailable,
    next,
    previous,
    goTo,
  };
};

export default usePagination;
